import type { OrchestratorSchemas } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 수동 이송(`IFC-RO-55~57`) — 후보 · 시작 · 잡 한 건.
 *
 * 실물(`openapi.json` · `ctcells-core/app/views/handoff.py` · `app/services/manual_transfer.py` · 2026-09-29)
 *
 * | 칸 | 후보 `GET /transfers/manual/options` | 시작 `POST /transfers/manual` | 잡 `GET /transfers/manual/{job_id}` |
 * | --- | --- | --- | --- |
 * | 본문 | — | `ManualTransferRequest{command_id, kind, source_point_id?, dest_point_id, reason, physically_checked: true}` | — |
 * | 헤더 | — | `X-Control-Token` 필수 | — |
 * | 성공 | 200 `ManualTransferOptions`(openapi 는 빈 스키마 — 실물 모양) | **202** `ManualTransferJob` | 200 `ManualTransferJob` |
 * | 거절 | — | 409 — 그 후보의 카탈로그 코드(`MODULE_BUSY` · `HANDOFF_POINT_OCCUPIED` · `HANDOFF_RESOURCE_IN_USE` …) · `context.reason` · `context.said` | 404 |
 *
 * 🔴 **화면은 판정하지 않는다** — 후보마다 `enabled` · `code` · `reason` 을 서버가 낸다. 고를 수 있는 자리는
 *    그 목록 안에서만 고른다(자리를 지어내지 않는다). 시작은 같은 판정을 다시 돌려 꺼진 후보면 그 코드로 거절한다.
 * 🔴 **시작은 장비로 아무것도 보내지 않는다** — 이송 시작 기록과 잡만 세우고(v0.9.4 — 자원 락은 걷혔다), 걸음은 스케줄러 틱이 민다. 결과는 잡의
 *    `status`(`running` → `done` · `halted` · `stopped`)와 `said` 가 말한다.
 * ⚠ `place_held` 는 로봇이 **쥔(`holding`)** 트레이만 놓는다. 쥔 것이 없으면 `robot_not_holding` 으로 꺼진다.
 */

export type ManualTransferOptions = OrchestratorSchemas['schemas']['ManualTransferOptions']
export type ManualTransferJob = OrchestratorSchemas['schemas']['ManualTransferJob']
export type ManualTransferKind = OrchestratorSchemas['schemas']['ManualTransferRequest']['kind']

export function readManualTransferOptions(): Promise<ManualTransferOptions> {
  return orchestrator.request<ManualTransferOptions>({
    method: 'GET',
    path: '/transfers/manual/options',
    retryOn401: true,
  })
}

export function readManualTransfer(jobId: string): Promise<ManualTransferJob> {
  return orchestrator.request<ManualTransferJob>({
    method: 'GET',
    path: `/transfers/manual/${encodeURIComponent(jobId)}`,
    retryOn401: true,
  })
}

export interface StartManualTransferVars {
  kind: ManualTransferKind
  /** `transfer` 만 싣는다 — `place_held` 에 실으면 서버가 422 */
  sourcePointId?: string
  destPointId: string
  /** 🔴 감사에 그대로 실린다 */
  reason: string
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

export function startManualTransfer({
  kind,
  sourcePointId,
  destPointId,
  reason,
  commandId,
}: StartManualTransferVars): Promise<ManualTransferJob> {
  return orchestrator.request<ManualTransferJob>({
    method: 'POST',
    path: '/transfers/manual',
    body: {
      command_id: commandId ?? newCommandId(),
      kind,
      ...(sourcePointId === undefined ? {} : { source_point_id: sourcePointId }),
      dest_point_id: destPointId,
      reason,
      // 계약이 `const: true` 다 — 이 요청을 보내는 쪽이 팔·문 상태를 봤다는 확약
      physically_checked: true,
    },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

export const useStartManualTransfer = createCommand<StartManualTransferVars, ManualTransferJob>({
  send: startManualTransfer,
  // 로봇·모듈이 움직이고 트레이 위치가 바뀐다 — 결과는 구독(`batch_tracking` 포함)과 잡 조회가 말한다
  invalidates: () => [keys.modules.all(), keys.tracking.all()],
})
